import { forwardRef, type Ref, type ReactNode, type InputHTMLAttributes } from 'react';
import { useStableId, useUncontrolled } from '@repletoryreach/sdk-utils/hooks';
import './Switch.css';

export interface SwitchProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'color' | 'onChange' | 'value' | 'type' | 'defaultChecked' | 'checked'> {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  onLabel?: ReactNode;
  offLabel?: ReactNode;
}

/** Toggle switch: visually distinct from Checkbox; same semantics. */
export const Switch = forwardRef(function Switch(
  { label, description, error, checked, defaultChecked, onChange, size = 'sm', onLabel, offLabel, id, name, disabled, ...inputProps }: SwitchProps,
  ref: Ref<HTMLInputElement>
): ReactNode {
  const auto = useStableId('rr-switch');
  const inputId = id ?? auto;
  const [val, setVal] = useUncontrolled<boolean>({
    value: checked, defaultValue: defaultChecked, finalValue: false, onChange
  });
  return (
    <label htmlFor={inputId} className={`rr-switch rr-switch--size-${size}`}>
      <input
        ref={ref}
        id={inputId}
        name={name}
        type="checkbox"
        role="switch"
        checked={val}
        disabled={disabled}
        onChange={(e) => setVal(e.currentTarget.checked)}
        aria-invalid={!!error || undefined}
        {...inputProps}
      />
      <span className="rr-switch__track" aria-hidden>
        {onLabel || offLabel ? (
          <span className="rr-switch__inline-label">{val ? onLabel : offLabel}</span>
        ) : null}
        <span className="rr-switch__thumb" />
      </span>
      <span className="rr-switch__body">
        {label ? <span className="rr-switch__label">{label}</span> : null}
        {description ? <span className="rr-switch__description">{description}</span> : null}
        {error ? <span className="rr-switch__error">{error}</span> : null}
      </span>
    </label>
  );
});
